import React, { useCallback, useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { MdDelete } from 'react-icons/md';

import DepotMobile from '../DepotMobile';
import Layout from '../Layout';
import {
  listerCompagnies, listerConditions, listerPieces, lireContrat, majContrat,
  supprimerPhoto, televerserPiece,
} from '../api';
import ChampDate from '../../components/ChampDate/ChampDate';

const CATEGORIE_CP = 'condition_particuliere';

/**
 * Le contrat du dossier : la compagnie, la police, les conditions générales.
 *
 * C'est la pièce qui manquait au parcours. Sans elle, on sait ce que les
 * travaux coûtent mais pas ce que le contrat couvre — ni les franchises, ni
 * les postes qui ne viennent d'aucun barème : relogement, frais d'expert,
 * recherche de fuite. Ceux-là sortent des limites du contrat, pas des prix.
 */
export default function Contrat({ expertiseId, onSuivant, onRetour, onEtape }) {
  const [contrat, setContrat] = useState(null);
  const [compagnies, setCompagnies] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [pieces, setPieces] = useState([]);
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');
  const [occupe, setOccupe] = useState(false);
  const [qrOuvert, setQrOuvert] = useState(false);

  useEffect(() => {
    let vivant = true;
    Promise.all([lireContrat(expertiseId), listerCompagnies()])
      .then(([c, cies]) => {
        if (!vivant) return;
        setContrat({
          reference: c.reference || '', numero_client: c.numero_client || '',
          startDate: c.startDate || '',
          compagnie_id: c.compagnie?.id ?? '',
          general_conditions: c.general_conditions ?? '',
        });
        setCompagnies(cies);
      })
      .catch(() => vivant && setErreur(
        "Impossible de charger votre contrat. S'il n'est pas encore rattaché "
        + 'au dossier, signalez-le nous.'));
    return () => { vivant = false; };
  }, [expertiseId]);

  const compagnieId = contrat?.compagnie_id;
  const chargerConditions = useCallback(() => {
    if (!compagnieId) { setConditions([]); return; }
    listerConditions(compagnieId).then(setConditions).catch(() => setConditions([]));
  }, [compagnieId]);

  useEffect(() => { chargerConditions(); }, [chargerConditions]);

  const rechargerPieces = useCallback(() => {
    listerPieces(expertiseId, CATEGORIE_CP).then(setPieces).catch(() => setPieces([]));
  }, [expertiseId]);

  useEffect(() => { rechargerPieces(); }, [rechargerPieces]);

  const deposer = async (fichiers) => {
    setOccupe(true);
    setErreur('');
    try {
      for (const f of fichiers) {
        await televerserPiece(expertiseId, f, CATEGORIE_CP);
      }
      rechargerPieces();
    } catch {
      setErreur("Le dépôt du document a échoué.");
    } finally {
      setOccupe(false);
    }
  };

  const retirerPiece = async (id) => {
    try {
      await supprimerPhoto(id);
      rechargerPieces();
    } catch {
      setErreur("La suppression n'a pas abouti.");
    }
  };

  const changer = (nom) => (e) =>
    setContrat((c) => ({ ...c, [nom]: e.target.value,
                         ...(nom === 'compagnie_id'
                             ? { general_conditions: '' } : {}) }));

  const enregistrer = async () => {
    setOccupe(true);
    setErreur('');
    setMessage('');
    try {
      await majContrat(expertiseId, {
        ...contrat,
        compagnie_id: contrat.compagnie_id || null,
        general_conditions: contrat.general_conditions || null,
        startDate: contrat.startDate || null,
      });
      setMessage('Votre contrat est enregistré.');
    } catch {
      setErreur("L'enregistrement n'a pas abouti. Réessayez.");
    } finally {
      setOccupe(false);
    }
  };

  return (
    <Layout
      etape="contrat"
      titre="Votre contrat"
      introduction="De quel contrat relève ce sinistre ? C'est lui qui dira ce qui est couvert, quelles franchises s'appliquent, et quels frais annexes vous pouvez réclamer."
      onEtape={onEtape}
      actions={
        <>
          <Button onClick={onRetour}>Retour</Button>
          <Button variant="contained" size="large"
                  onClick={async () => { await enregistrer(); onSuivant(); }}
                  disabled={occupe || !contrat}>
            Continuer
          </Button>
        </>
      }
    >
      {erreur && <Alert severity="error" sx={{ mb: 3 }}>{erreur}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 3 }}>{message}</Alert>}
      {!contrat && !erreur && <CircularProgress size={28} />}

      {contrat && (
        <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3.5 } }}>
          <Stack spacing={3}>
            <Autocomplete
              options={compagnies}
              value={compagnies.find((c) => c.id === contrat.compagnie_id) || null}
              onChange={(e, choix) => setContrat((c) => ({
                ...c, compagnie_id: choix?.id ?? '', general_conditions: '',
              }))}
              getOptionLabel={(c) => c.name || ''}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              noOptionsText="Aucune compagnie de ce nom"
              renderInput={(params) => (
                <TextField {...params} label="Votre compagnie d'assurance"
                           placeholder="Tapez les premières lettres…"
                           helperText="Elle figure en tête de votre police." />
              )}
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Numéro de police" value={contrat.reference}
                         onChange={changer('reference')} fullWidth
                         helperText="Le numéro du contrat lui-même." />
              <TextField label="Numéro de client" value={contrat.numero_client}
                         onChange={changer('numero_client')} fullWidth
                         helperText="Certaines compagnies ne donnent leurs conditions générales qu'à ce numéro." />
            </Stack>

            <ChampDate
              label="Date de souscription"
              value={contrat.startDate} onChange={changer('startDate')}
              InputLabelProps={{ shrink: true }}
              sx={{ maxWidth: 260 }}
            />

            <TextField
              select label="Vos conditions générales"
              value={contrat.general_conditions}
              onChange={changer('general_conditions')}
              disabled={!contrat.compagnie_id}
              helperText={contrat.compagnie_id
                ? "Leur référence figure en bas de votre police. C'est ce document qui permet de citer un article précis."
                : "Choisissez d'abord votre compagnie."}
            >
              <MenuItem value="">Je ne les ai pas encore</MenuItem>
              {conditions.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </TextField>

            {contrat.compagnie_id && conditions.length === 0 && (
              <Alert severity="info">
                Nous n'avons pas encore les conditions générales de cette
                compagnie. Votre dossier reste utilisable : l'analyse du
                contrat se fera quand elles seront disponibles.
              </Alert>
            )}

            <Box>
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                Vos conditions particulières
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Les pages propres à votre contrat : capitaux assurés,
                franchises, options souscrites. Ce sont elles qui priment sur
                les conditions générales quand les deux se contredisent.
              </Typography>

              {pieces.length > 0 && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap
                       sx={{ mb: 2 }}>
                  {pieces.map((p) => (
                    <Chip key={p.id} variant="outlined"
                          label={p.title || 'Document'}
                          onDelete={() => retirerPiece(p.id)}
                          deleteIcon={<MdDelete />} />
                  ))}
                </Stack>
              )}

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button component="label" variant="outlined" disabled={occupe}>
                  Choisir un fichier
                  <input type="file" hidden multiple
                         accept="image/*,application/pdf"
                         onChange={(e) => deposer([...e.target.files])} />
                </Button>
                {/* `capture` ouvre l'appareil photo sur mobile. Sur un PC
                    il n'y en a pas : le QR passe la main au téléphone. */}
                <Button component="label" variant="outlined" disabled={occupe}>
                  Photographier
                  <input type="file" hidden accept="image/*" capture="environment"
                         onChange={(e) => deposer([...e.target.files])} />
                </Button>
                <Button variant="outlined" disabled={occupe}
                        onClick={() => setQrOuvert(true)}>
                  Avec mon téléphone
                </Button>
              </Stack>
            </Box>

            <Typography variant="body2" color="text.secondary">
              Vous pouvez continuer sans ces informations et y revenir plus
              tard — mais sans contrat, nous ne pouvons ni vérifier vos
              franchises ni dire ce qui est couvert.
            </Typography>

            <Stack direction="row" justifyContent="flex-end">
              <Button variant="outlined" onClick={enregistrer} disabled={occupe}>
                Enregistrer
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      <DepotMobile
        ouvert={qrOuvert}
        onFermer={() => { setQrOuvert(false); rechargerPieces(); }}
        expertiseId={expertiseId}
        categorie={CATEGORIE_CP}
        onDepot={rechargerPieces}
      />
    </Layout>
  );
}
